import { maintainAccounts } from '../accountMaintenanceClient';
import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Clock, Users, CheckCircle, AlertCircle, MessageSquare, BarChart3, FileText, Plus, Send, ThumbsUp, ThumbsDown, User, Settings, LogOut, ChevronRight, ChevronDown, ChevronUp, X, Mic, MicOff, Mail, Search, RefreshCw, Trash2, XCircle, ArrowLeft, Loader2, Bell, BellOff, Eye, Download, Filter, Columns, ArrowUpDown, Maximize2, Minimize2, ChevronLeft, ShieldAlert, Shield, ShieldOff, Heart, Phone, Info, Vote, Pencil, Upload } from 'lucide-react';
import { MOOD_EMOJIS, MOOD_LABELS } from '../modules/freemium/mood/MoodConfig.js';
import { VOTE_STATUS, VOTE_RATING_LABELS } from '../modules/freemium/vote/VoteConfig.js';
import { useToast, useCrisisAlert, ToastProvider, CrisisContext, loadVisibleTenants } from '../shared.jsx';
import { TeamMoodOverview } from './MoodComponents.jsx';
import ModalDialog from './ModalDialog.jsx';
import { storage, addAuditEntry, calculateWorkingHours, OWL_LOGO, EMAIL_CONFIG, detectHarmContent, notifyMentorOfHarmDetection, sendNotification, VoiceInputButton, useVoiceInput, initFCM, removeFCMToken, notificationPermissionGranted, requestNotificationPermission, CrisisResourceModal, getUrlParams, clearUrlParams, initFirebase, loadAllRelationships, saveRelationship, deriveConflictAccess, computeRelationalAccessSnapshot, getTenantUsersKey, useRelationships, getRoleLabel, checkSeatCapacity, loadTenant, EMAIL_NOTIF_CATEGORIES } from '../shared.jsx';

function UserManagementView({ user, conflicts, onBack, onRefresh, viewingTenantId, viewingTenantName }) {
  const toast = useToast();
  const { refreshRelationships } = useRelationships();
  // S4: tenant scoping — support can pass viewingTenantId to manage any tenant
  const effectiveTenantId = viewingTenantId || user.tenantId || 'tenant-public';
  const effectiveTenantName = viewingTenantName || null;
  const USERS_KEY = getTenantUsersKey(effectiveTenantId);
  const [allUsers, setAllUsers] = useState([]);
  const [allConflicts, setAllConflicts] = useState(conflicts || []); // Initialize with passed conflicts
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassignData, setReassignData] = useState({ conflict: null, role: null, currentUser: null });
  const [showAddToConflictModal, setShowAddToConflictModal] = useState(false);
  const [addToConflictUser, setAddToConflictUser] = useState(null);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [newUserData, setNewUserData] = useState({ name: '', email: '', role: 'mentee' });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('name-asc');
  const [actionLoading, setActionLoading] = useState(null);
  // Merge user state
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeSourceUser, setMergeSourceUser] = useState(null);
  const [mergeTargetUser, setMergeTargetUser] = useState(null);
  const [duplicateUsers, setDuplicateUsers] = useState([]);
  const [agreementSignatures, setAgreementSignatures] = useState({}); // { userId: agreementRecord }
  const [userBillingMap, setUserBillingMap] = useState({}); // { userId: { plan, planStatus, ... } }
  const [allRelationships, setAllRelationships] = useState([]);
  const [addRelForUser, setAddRelForUser] = useState(null); // userId of user we're adding a connection for
  const [addRelEmail, setAddRelEmail] = useState('');
  const [addRelType, setAddRelType] = useState('mentor-participant');
  const [addRelRole, setAddRelRole] = useState(''); // which side the target user plays
  const [addRelLoading, setAddRelLoading] = useState(false);
  // Free Tools data
  const [freeToolsExpanded, setFreeToolsExpanded] = useState(null); // userId
  const [freeToolsData, setFreeToolsData] = useState({}); // { votes, siyw, mood }
  const [freeToolsLoading, setFreeToolsLoading] = useState(false);
  const [freeToolsSection, setFreeToolsSection] = useState('votes'); // 'votes' | 'siyw' | 'mood'
  const [editingFreeItem, setEditingFreeItem] = useState(null); // { type, id, field, value }
  const [freeToolsCounts, setFreeToolsCounts] = useState({}); // { [userId]: { votes, siyw, mood } }
  // Bulk upload state
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [bulkStep, setBulkStep] = useState('input'); // 'input' | 'preview' | 'complete'
  const [bulkCsvText, setBulkCsvText] = useState('');
  const [bulkParsedRows, setBulkParsedRows] = useState([]); // { name, email, role, mentorEmail, status, error }
  const [bulkResults, setBulkResults] = useState({ created: 0, skipped: 0, relationships: 0, errors: [] });
  const [bulkLoading, setBulkLoading] = useState(false);

  // Load all users and all conflicts from storage
  useEffect(() => {
    loadAllData();
  }, []);

  // Update allConflicts when conflicts prop changes
  useEffect(() => {
    if (conflicts && conflicts.length > 0) {
      setAllConflicts(conflicts);
    }
  }, [conflicts]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      // Load all users
      const allUsersList = await storage.get(USERS_KEY, true) || [];
      setAllUsers(Array.isArray(allUsersList) ? allUsersList : []);
      
      // Load agreement signatures for all users
      try {
        const sigMap = {};
        const usersList = Array.isArray(allUsersList) ? allUsersList : [];
        for (const u of usersList) {
          try {
            const sig = await storage.get(`agreement-signed:${u.id}`, true);
            if (sig) sigMap[u.id] = sig;
          } catch (e) { /* user hasn't signed */ }
        }
        setAgreementSignatures(sigMap);
      } catch (sigErr) {
        console.error('Error loading agreement signatures:', sigErr);
      }
      
      // Load billing data for all users (non-blocking)
      try {
        const billingMap = {};
        const usersList2 = Array.isArray(allUsersList) ? allUsersList : [];
        for (const u of usersList2) {
          try {
            const billing = await storage.get(`stripe-user:${u.id}`, true);
            if (billing) billingMap[u.id] = billing;
          } catch (e) { /* no billing data */ }
        }
        setUserBillingMap(billingMap);
      } catch (e) { /* best effort */ }

      // Load ALL conflicts from shared storage (admin sees everything)
      try {
        const conflictKeys = await storage.list('conflict:', true);
        const loadedConflicts = [];
        const keysArray = Array.isArray(conflictKeys) ? conflictKeys : [];
        for (const key of keysArray) {
          try {
            const conflict = await storage.get(key, true);
            if (conflict && (conflict.tenantId === effectiveTenantId || (!conflict.tenantId && effectiveTenantId === 'tenant-hci'))) {
              loadedConflicts.push(conflict);
            }
          } catch (e) {
            console.error('Error loading conflict:', key, e);
          }
        }
        if (loadedConflicts.length > 0) {
          setAllConflicts(loadedConflicts);
        }
      } catch (conflictError) {
        console.error('Error loading conflicts, using passed conflicts:', conflictError);
        // Fall back to passed conflicts prop
      }
      
      // Load all relationships
      try {
        const rels = await loadAllRelationships();
        setAllRelationships(rels);
      } catch (relErr) {
        console.error('Error loading relationships:', relErr);
      }

      // Load free tools counts in background (non-blocking)
      const usersList = Array.isArray(allUsersList) ? allUsersList : [];
      if (usersList.length > 0) loadFreeToolsCounts(usersList);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Reload function for after changes
  const reloadData = async () => {
    await loadAllData();
    if (onRefresh) onRefresh();
  };

  // Get conflicts for a specific user
  const getUserConflicts = (userId) => {
    return allConflicts.filter(c => {
      const isMentee = c.mentees?.some(m => m.id === userId);
      const isFlyOnWall = c.flyOnWall?.id === userId;
      const isOmniscient = c.omniscient?.id === userId;
      const isCreator = c.createdBy === userId;
      return isMentee || isFlyOnWall || isOmniscient || isCreator;
    });
  };

  // Load free tools counts for all users (background, non-blocking)
  const loadFreeToolsCounts = async (usersList) => {
    const counts = {};
    for (const u of usersList) {
      try {
        const [votes, siyw, moodRaw] = await Promise.all([
          storage.get(`vote:${u.id}`, true).catch(() => null),
          storage.get(`siyw-sessions:${u.id}`, true).catch(() => null),
          storage.get(`mood:${u.id}`, false).catch(() => null),
        ]);
        const mood = moodRaw ? (typeof moodRaw === 'string' ? JSON.parse(moodRaw) : moodRaw) : {};
        counts[u.id] = {
          votes: Object.keys(votes || {}).length,
          siyw: Object.keys(siyw || {}).length,
          mood: Object.keys(mood || {}).length,
        };
      } catch {
        counts[u.id] = { votes: 0, siyw: 0, mood: 0 };
      }
    }
    setFreeToolsCounts(counts);
  };

  // Load free tools data for a user
  const loadFreeToolsData = async (userId) => {
    setFreeToolsLoading(true);
    try {
      const [votes, siyw, moodRaw] = await Promise.all([
        storage.get(`vote:${userId}`, true).catch(() => null),
        storage.get(`siyw-sessions:${userId}`, true).catch(() => null),
        storage.get(`mood:${userId}`, false).catch(() => null),
      ]);
      const mood = moodRaw ? (typeof moodRaw === 'string' ? JSON.parse(moodRaw) : moodRaw) : {};
      setFreeToolsData({ votes: votes || {}, siyw: siyw || {}, mood: mood || {} });
      // Update line-card counts for this user
      setFreeToolsCounts(prev => ({ ...prev, [userId]: { votes: Object.keys(votes || {}).length, siyw: Object.keys(siyw || {}).length, mood: Object.keys(mood || {}).length } }));
    } catch (e) {
      console.error('[FreeTools] Load error:', e);
      setFreeToolsData({ votes: {}, siyw: {}, mood: {} });
    }
    setFreeToolsLoading(false);
  };

  // Delete a free tool item
  const handleDeleteFreeItem = async (userId, type, itemId) => {
    const labels = { votes: 'vote', siyw: 'SIYW session', mood: 'mood check-in' };
    if (!window.confirm(`Delete this ${labels[type]}? This cannot be undone.`)) return;
    setActionLoading(`free-${type}-${itemId}`);
    try {
      if (type === 'votes') {
        const votes = await storage.get(`vote:${userId}`, true) || {};
        delete votes[itemId];
        await storage.set(`vote:${userId}`, votes, true);
      } else if (type === 'siyw') {
        const sessions = await storage.get(`siyw-sessions:${userId}`, true) || {};
        delete sessions[itemId];
        await storage.set(`siyw-sessions:${userId}`, sessions, true);
      } else if (type === 'mood') {
        const moodRaw = await storage.get(`mood:${userId}`, false);
        const mood = moodRaw ? (typeof moodRaw === 'string' ? JSON.parse(moodRaw) : moodRaw) : {};
        delete mood[itemId];
        await storage.set(`mood:${userId}`, JSON.stringify(mood), false);
      }
      await loadFreeToolsData(userId);
      toast(`${labels[type]} deleted.`);
    } catch (e) {
      console.error('[FreeTools] Delete error:', e);
      toast('Delete failed. See console.', 'error');
    }
    setActionLoading(null);
  };

  // Save an edited free tool item field
  const handleSaveFreeItem = async (userId, type, itemId, field, value) => {
    setActionLoading(`free-${type}-${itemId}`);
    try {
      if (type === 'votes') {
        const votes = await storage.get(`vote:${userId}`, true) || {};
        if (votes[itemId]) {
          votes[itemId][field] = value;
          await storage.set(`vote:${userId}`, votes, true);
        }
      } else if (type === 'siyw') {
        const sessions = await storage.get(`siyw-sessions:${userId}`, true) || {};
        if (sessions[itemId]) {
          if (field === 'extracted.what' || field === 'extracted.who' || field === 'extracted.feelings') {
            const parts = field.split('.');
            if (!sessions[itemId].extracted) sessions[itemId].extracted = {};
            sessions[itemId].extracted[parts[1]] = value;
          } else {
            sessions[itemId][field] = value;
          }
          await storage.set(`siyw-sessions:${userId}`, sessions, true);
        }
      } else if (type === 'mood') {
        const moodRaw = await storage.get(`mood:${userId}`, false);
        const mood = moodRaw ? (typeof moodRaw === 'string' ? JSON.parse(moodRaw) : moodRaw) : {};
        if (mood[itemId]) {
          mood[itemId][field] = value;
          await storage.set(`mood:${userId}`, JSON.stringify(mood), false);
        }
      }
      await loadFreeToolsData(userId);
      setEditingFreeItem(null);
      toast('Saved.');
    } catch (e) {
      console.error('[FreeTools] Save error:', e);
      toast('Save failed. See console.', 'error');
    }
    setActionLoading(null);
  };

  // Toggle user active status
  const toggleUserStatus = async (targetUser) => {
    if (targetUser.id === user.id) {
      toast("You cannot deactivate your own account!");
      return;
    }
    
    setActionLoading(targetUser.id);
    try {
      const newStatus = targetUser.isActive === false ? true : false;

      // P5: Check seat capacity before reactivating
      if (newStatus) {
        const tenant = await loadTenant(effectiveTenantId);
        const seatCheck = checkSeatCapacity(tenant);
        if (!seatCheck.allowed) {
          toast(seatCheck.reason);
          setActionLoading(null);
          return;
        }
      }

      const updatedUser = { ...targetUser, isActive: newStatus };
      await storage.set(`user:${targetUser.id}`, updatedUser, true);

      // S4: update tenant users list
      const allUsersList = await storage.get(USERS_KEY, true) || [];
      const updatedAllUsers = allUsersList.map(u =>
        u.id === targetUser.id ? { ...u, isActive: newStatus } : u
      );
      await storage.set(USERS_KEY, updatedAllUsers, true);


      setAllUsers(prev => prev.map(u =>
        u.id === targetUser.id ? updatedUser : u
      ));

      // P3: When deactivating a non-participant user, inactivate their active relationships
      if (!newStatus) {
        try {
          const allRels = await loadAllRelationships();
          const activeRelsForUser = allRels.filter(r =>
            r.status === 'active' &&
            (r.initiatorId === targetUser.id || r.receiverId === targetUser.id)
          );
          if (activeRelsForUser.length > 0) {
            // Stamp affected conflicts with access snapshot before revoking
            try {
              const allConflictData = await storage.list('conflict:', true);
              for (const rel of activeRelsForUser) {
                const revokedUserId = rel.initiatorId === targetUser.id ? rel.receiverId : rel.initiatorId;
                const revokedUserName = rel.initiatorId === targetUser.id ? rel.receiverName : rel.initiatorName;
                const revokedUserEmail = rel.initiatorId === targetUser.id ? rel.receiverEmail : rel.initiatorEmail;
                const revokedRole = rel.initiatorId === targetUser.id ? rel.receiverRole : rel.initiatorRole;
                const affectedConflicts = (allConflictData || []).filter(c =>
                  deriveConflictAccess(c, { id: revokedUserId, email: revokedUserEmail, role: revokedRole }, allRels).canRead
                );
                for (const c of affectedConflicts) {
                  const accessBefore = computeRelationalAccessSnapshot(c, allRels);
                  const details = `User deactivated: ${targetUser.name} (${targetUser.email}). Relationship ${targetUser.name} ↔ ${revokedUserName} revoked. Access before: ${accessBefore.length > 0 ? accessBefore.map(a => `${a.name} (${a.email || 'no email'}): ${a.accessPath}`).join('; ') : 'None'}`;
                  addAuditEntry(c, user.id, user.name, 'Relationship revoked: access snapshot', details, user.email);
                  await storage.set(`conflict:${c.id}`, c, true);
                }
              }
            } catch (snapErr) {
              console.warn('[P3] Relationship access snapshot failed:', snapErr);
            }

            let updatedRels = allRels;
            for (const rel of activeRelsForUser) {
              const inactivated = { ...rel, status: 'inactive', revokedAt: new Date().toISOString(), revokedBy: user.id };
              updatedRels = await saveRelationship(inactivated, updatedRels, rel.tenantId || effectiveTenantId);
            }
            toast(`${activeRelsForUser.length} connection${activeRelsForUser.length !== 1 ? 's' : ''} inactivated.`);
          }
        } catch (relErr) {
          console.warn('[P3] Failed to inactivate relationships:', relErr);
        }
      }
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast('Failed to update user status');
    } finally {
      setActionLoading(null);
    }
  };

  // Toggle one email-notification category for a user
  const toggleEmailPref = async (targetUser, prefKey) => {
    try {
      const newValue = !(targetUser[prefKey] !== false);
      const updatedUser = { ...targetUser, [prefKey]: newValue };
      await storage.set(`user:${targetUser.id}`, updatedUser, true);
      const allUsersList = await storage.get(USERS_KEY, true) || [];
      const updatedAllUsers = allUsersList.map(u =>
        u.id === targetUser.id ? { ...u, [prefKey]: newValue } : u
      );
      await storage.set(USERS_KEY, updatedAllUsers, true);
      setAllUsers(prev => prev.map(u => u.id === targetUser.id ? updatedUser : u));
      toast(`Email preference updated for ${targetUser.name}`);
    } catch (error) {
      console.error('Error toggling email preference:', error);
      toast('Failed to update email preference');
    }
  };

  // Change user role
  const changeUserRole = async (targetUser, newRole) => {
    if (targetUser.id === user.id && newRole !== 'admin') {
      if (!confirm("Are you sure you want to remove your own admin privileges?")) {
        return;
      }
    }
    
    setActionLoading(targetUser.id);
    try {
      const updatedUser = { ...targetUser, role: newRole };
      await storage.set(`user:${targetUser.id}`, updatedUser, true);
      
      // S4: update tenant users list
      const allUsersList = await storage.get(USERS_KEY, true) || [];
      const updatedAllUsers = allUsersList.map(u => 
        u.id === targetUser.id ? { ...u, role: newRole } : u
      );
      await storage.set(USERS_KEY, updatedAllUsers, true);
      
      setAllUsers(prev => prev.map(u => 
        u.id === targetUser.id ? updatedUser : u
      ));
    } catch (error) {
      console.error('Error changing user role:', error);
      toast('Failed to update user role');
    } finally {
      setActionLoading(null);
    }
  };

  // Delete user permanently
  const deleteUser = async (targetUser) => {
    if (targetUser.id === user.id) {
      toast("You cannot delete your own account!");
      return;
    }
    
    // Check if user is involved in any conflicts
    const userConflicts = getUserConflicts(targetUser.id);
    
    let confirmMsg = `Are you sure you want to permanently delete "${targetUser.name}"?\n\nThis action cannot be undone.`;
    
    if (userConflicts.length > 0) {
      confirmMsg = `WARNING: "${targetUser.name}" is involved in ${userConflicts.length} conflict(s).\n\n` +
        `Deleting this user will:\n` +
        `• Remove them from all conflicts they're participating in\n` +
        `• Their contributions (comments, alternatives, etc.) will remain but be orphaned\n\n` +
        `Consider deactivating instead if you want to preserve their association with conflicts.\n\n` +
        `Are you sure you want to permanently delete this user?`;
    }
    
    if (!confirm(confirmMsg)) {
      return;
    }
    
    // Double confirmation for users with conflicts
    if (userConflicts.length > 0) {
      if (!confirm(`FINAL CONFIRMATION: Delete "${targetUser.name}" and remove them from ${userConflicts.length} conflict(s)?`)) {
        return;
      }
    }
    
    setActionLoading(`delete-${targetUser.id}`);
    try {
      await maintainAccounts({ action: 'delete-user', tenantId: effectiveTenantId, sourceId: targetUser.id });

      // Update local state
      setAllUsers(prev => prev.filter(u => u.id !== targetUser.id));
      
      // Reload data to reflect conflict changes
      await reloadData();
      
      toast(`User "${targetUser.name}" has been permanently deleted.`);
    } catch (error) {
      console.error('Error deleting user:', error);
      toast(error.message || 'Failed to delete user.');
    } finally {
      setActionLoading(null);
    }
  };

  // Delete conflict permanently
  const deleteConflict = async (conflict) => {
    const confirmMsg = `Are you sure you want to permanently delete the conflict "${conflict.title}"?\n\n` +
      `This will remove:\n` +
      `• All participant assignments\n` +
      `• All communication history\n` +
      `• All proposed alternatives\n` +
      `• All ratings and acknowledgments\n\n` +
      `This action cannot be undone.`;
    
    if (!confirm(confirmMsg)) {
      return;
    }
    
    setActionLoading(`delete-conflict-${conflict.id}`);
    try {
      // Delete the conflict from storage
      await storage.delete(`conflict:${conflict.id}`, true);
      
      // Update local state
      setAllConflicts(prev => prev.filter(c => c.id !== conflict.id));
      
      // Refresh parent data
      if (onRefresh) onRefresh();
      
      toast(`Conflict "${conflict.title}" has been permanently deleted.`);
    } catch (error) {
      console.error('Error deleting conflict:', error);
      toast('Failed to delete conflict. Check console for details.');
    } finally {
      setActionLoading(null);
    }
  };

  // Remove user from conflict
  const removeFromConflict = async (conflict, roleType, targetUserId) => {
    setActionLoading(`${conflict.id}-${roleType}`);
    try {
      await maintainAccounts({ action: 'remove', tenantId: effectiveTenantId, conflictId: conflict.id, role: roleType, sourceId: targetUserId, updatedAt: conflict.updatedAt });

      // Refresh conflicts
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error removing user from conflict:', error);
      toast(error.message || 'Failed to remove user from conflict');
    } finally {
      setActionLoading(null);
    }
  };

  // Open reassign modal
  const openReassignModal = (conflict, role, currentUserId) => {
    setReassignData({ conflict, role, currentUser: currentUserId });
    setShowReassignModal(true);
  };

  // Reassign user to conflict
  const reassignUser = async (newUserId) => {
    const { conflict, role, currentUser } = reassignData;
    if (!conflict || !role) return;
    
    setActionLoading(`reassign-${conflict.id}`);
    try {
      await maintainAccounts({ action: 'assign', tenantId: effectiveTenantId, conflictId: conflict.id, role, sourceId: currentUser || undefined, targetId: newUserId, updatedAt: conflict.updatedAt });

      setShowReassignModal(false);
      setReassignData({ conflict: null, role: null, currentUser: null });
      
      // Refresh conflicts
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error reassigning user:', error);
      toast(error.message || 'Failed to reassign user');
    } finally {
      setActionLoading(null);
    }
  };

  // Add user to conflict
  const addUserToConflict = async (conflictId, roleInConflict) => {
    if (!addToConflictUser) return;
    
    const conflict = allConflicts.find(c => c.id === conflictId);
    if (!conflict) {
      toast('Conflict not found');
      return;
    }
    
    setActionLoading(`add-${conflictId}`);
    try {
      await maintainAccounts({ action: 'assign', tenantId: effectiveTenantId, conflictId, role: roleInConflict, targetId: addToConflictUser.id, updatedAt: conflict.updatedAt });

      setShowAddToConflictModal(false);
      setAddToConflictUser(null);
      
      // Reload all data to reflect changes
      await reloadData();
    } catch (error) {
      console.error('Error adding user to conflict:', error);
      toast(error.message || 'Failed to add user to conflict');
    } finally {
      setActionLoading(null);
    }
  };

  // Create new user (admin function)
  const createUser = async () => {
    const { name, email, role } = newUserData;
    
    if (!name.trim() || !email.trim()) {
      toast('Please fill in name and email');
      return;
    }
    
    // Check if email already exists
    if (allUsers.some(u => u.email.toLowerCase() === email.toLowerCase())) {
      toast('A user with this email already exists');
      return;
    }
    
    setActionLoading('create-user');
    try {
      // P5: Check seat capacity before creating user
      const tenant = await loadTenant(effectiveTenantId);
      const seatCheck = checkSeatCapacity(tenant);
      if (!seatCheck.allowed) {
        toast(seatCheck.reason);
        setActionLoading(null);
        return;
      }

      const newUser = {
        id: `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: name.trim(),
        email: email.trim().toLowerCase(),
        role: role,
        isActive: true,
        createdAt: new Date().toISOString(),
        tenantId: effectiveTenantId,
        createdBy: user.id
      };

      // S4: add to tenant users list
      const updatedAllUsers = [...allUsers, newUser];
      await storage.set(USERS_KEY, updatedAllUsers, true);


      // Update local state
      setAllUsers(updatedAllUsers);

      // Reset form and close modal
      setNewUserData({ name: '', email: '', role: 'mentee' });
      setShowCreateUserModal(false);

      toast(`User "${newUser.name}" created successfully!`);
    } catch (error) {
      console.error('Error creating user:', error);
      toast('Failed to create user');
    } finally {
      setActionLoading(null);
    }
  };

  // ── Bulk Upload: CSV parsing ──
  const ROLE_MAP = { participant: 'mentee', mentee: 'mentee', mentor: 'mentor', observer: 'fly-on-wall', 'fly-on-the-wall': 'fly-on-wall', 'fly-on-wall': 'fly-on-wall', global: 'omniscient', omniscient: 'omniscient', admin: 'admin' };

  const parseBulkCsv = (text) => {
    const lines = text.trim().split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) { toast('CSV must have a header row and at least one data row'); return; }

    const header = lines[0].toLowerCase().split(',').map(h => h.trim());
    const nameIdx = header.indexOf('name');
    const emailIdx = header.indexOf('email');
    const roleIdx = header.indexOf('role');
    const mentorIdx = header.indexOf('mentor_email');

    if (nameIdx === -1 || emailIdx === -1) { toast('CSV must have "name" and "email" columns'); return; }

    const existingEmails = new Set(allUsers.map(u => u.email.toLowerCase()));
    const seenInBatch = new Set();
    const rows = [];

    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(',').map(c => c.trim());
      const name = cols[nameIdx] || '';
      const email = (cols[emailIdx] || '').toLowerCase();
      const rawRole = (roleIdx !== -1 ? cols[roleIdx] : 'participant').toLowerCase();
      const mentorEmail = mentorIdx !== -1 ? (cols[mentorIdx] || '').toLowerCase() : '';
      const mappedRole = ROLE_MAP[rawRole];

      let status = 'ready';
      let error = '';

      if (!name || !email) { status = 'error'; error = 'Missing name or email'; }
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { status = 'error'; error = 'Invalid email format'; }
      else if (!mappedRole) { status = 'error'; error = `Unknown role "${rawRole}"`; }
      else if (existingEmails.has(email)) { status = 'skip'; error = 'Already exists in tenant'; }
      else if (seenInBatch.has(email)) { status = 'error'; error = 'Duplicate in CSV'; }

      if (status === 'ready') seenInBatch.add(email);
      rows.push({ name, email, role: mappedRole || rawRole, mentorEmail, status, error });
    }

    setBulkParsedRows(rows);
    setBulkStep('preview');
  };

  const handleBulkFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => { setBulkCsvText(ev.target.result); };
    reader.readAsText(file);
  };

  const executeBulkUpload = async () => {
    const readyRows = bulkParsedRows.filter(r => r.status === 'ready');
    if (readyRows.length === 0) { toast('No valid rows to upload'); return; }

    setBulkLoading(true);
    const tenant = await loadTenant(effectiveTenantId);
    const seatCheck = checkSeatCapacity(tenant);
    const currentActive = allUsers.filter(u => u.isActive !== false).length;
    const availableSeats = seatCheck.maxSeats === Infinity ? Infinity : seatCheck.maxSeats - currentActive;

    if (availableSeats < readyRows.length) {
      toast(`Only ${availableSeats} seat(s) available but ${readyRows.length} users to create. Reduce the CSV or increase seats.`);
      setBulkLoading(false);
      return;
    }

    const ANIMAL_LIST_LOCAL = ['bunny', 'fox', 'deer', 'bear', 'raccoon', 'squirrel', 'bluebird', 'turtle', 'otter', 'hedgehog'];
    let created = 0, skipped = 0, relCount = 0;
    const errors = [];
    const newUsers = [];
    const emailToUser = {};

    // Build email→user map from existing users
    allUsers.forEach(u => { emailToUser[u.email.toLowerCase()] = u; });

    // Create all users first
    for (const row of readyRows) {
      try {
        const newUser = {
          id: `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          name: row.name.trim(),
          email: row.email.trim().toLowerCase(),
          role: row.role,
          avatarAnimal: ANIMAL_LIST_LOCAL[Math.floor(Math.random() * ANIMAL_LIST_LOCAL.length)],
          isActive: true,
          createdAt: new Date().toISOString(),
          tenantId: effectiveTenantId,
          createdBy: user.id
        };
        newUsers.push(newUser);
        emailToUser[newUser.email] = newUser;
        created++;
      } catch (err) {
        errors.push(`${row.email}: ${err.message}`);
      }
    }

    // Save all users at once
    if (newUsers.length > 0) {
      const updatedAllUsers = [...allUsers, ...newUsers];
      await storage.set(USERS_KEY, updatedAllUsers, true);
      setAllUsers(updatedAllUsers);

    }

    // Create relationships for rows with mentor_email
    for (const row of readyRows) {
      if (!row.mentorEmail) continue;
      const mentorUser = emailToUser[row.mentorEmail];
      const menteeUser = emailToUser[row.email];
      if (!mentorUser) { errors.push(`${row.email}: mentor "${row.mentorEmail}" not found`); continue; }
      if (!menteeUser) continue;

      try {
        const rel = {
          id: `rel_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          type: 'mentor-participant',
          initiatorId: menteeUser.id,
          receiverId: mentorUser.id,
          initiatorRole: 'mentee',
          receiverRole: 'mentor',
          status: 'active',
          createdAt: new Date().toISOString(),
          acceptedAt: new Date().toISOString(),
          tenantId: effectiveTenantId,
          adminCreated: true
        };
        await saveRelationship(rel);
        relCount++;
      } catch (err) {
        errors.push(`Relationship ${row.email} → ${row.mentorEmail}: ${err.message}`);
      }
    }

    skipped = bulkParsedRows.filter(r => r.status === 'skip').length;
    setBulkResults({ created, skipped, relationships: relCount, errors });
    setBulkStep('complete');
    setBulkLoading(false);
    if (refreshRelationships) refreshRelationships();
  };

  const resetBulkUpload = () => {
    setShowBulkUploadModal(false);
    setBulkStep('input');
    setBulkCsvText('');
    setBulkParsedRows([]);
    setBulkResults({ created: 0, skipped: 0, relationships: 0, errors: [] });
  };

  // Find users with duplicate emails
  const findDuplicateEmails = () => {
    const emailMap = {};
    allUsers.forEach(u => {
      const email = u.email?.toLowerCase();
      if (email) {
        if (!emailMap[email]) {
          emailMap[email] = [];
        }
        emailMap[email].push(u);
      }
    });
    
    // Return only emails with more than one user
    const duplicates = Object.entries(emailMap)
      .filter(([_, users]) => users.length > 1)
      .map(([email, users]) => ({ email, users }));
    
    return duplicates;
  };

  // Open merge modal for a specific user
  const openMergeModal = (sourceUser) => {
    // Find other users with the same email
    const sameEmailUsers = allUsers.filter(
      u => u.email?.toLowerCase() === sourceUser.email?.toLowerCase() && u.id !== sourceUser.id
    );
    
    if (sameEmailUsers.length === 0) {
      toast('No other users found with the same email address.');
      return;
    }
    
    setMergeSourceUser(sourceUser);
    setMergeTargetUser(null);
    setShowMergeModal(true);
  };

  // Merge users: transfer all records from source to target, then delete source
  const mergeUsers = async () => {
    if (!mergeSourceUser || !mergeTargetUser) {
      toast('Please select both source and target users');
      return;
    }
    
    if (mergeSourceUser.id === mergeTargetUser.id) {
      toast('Cannot merge a user with themselves');
      return;
    }
    
    if (mergeSourceUser.id === user.id) {
      toast('Cannot merge away your own account. Select yourself as the target user instead.');
      return;
    }
    
    const confirmMsg = `Are you sure you want to merge "${mergeSourceUser.name}" into "${mergeTargetUser.name}"?\n\n` +
      `This will:\n` +
      `• Transfer all conflict participations to "${mergeTargetUser.name}"\n` +
      `• Delete the account "${mergeSourceUser.name}"\n\n` +
      `This action cannot be undone.`;
    
    if (!confirm(confirmMsg)) {
      return;
    }
    
    setActionLoading('merge-users');
    try {
      
      const { conflictsUpdated } = await maintainAccounts({ action: 'merge', tenantId: effectiveTenantId, sourceId: mergeSourceUser.id, targetId: mergeTargetUser.id });

      // Close modal
      setShowMergeModal(false);
      setMergeSourceUser(null);
      setMergeTargetUser(null);
      
      // Reload data to reflect changes
      await reloadData();
      
      toast(`Merge complete!\n\n` +
        `• "${mergeSourceUser.name}" has been merged into "${mergeTargetUser.name}"\n` +
        `• ${conflictsUpdated} conflict(s) were updated\n` +
        `• The duplicate account has been removed`);
      
    } catch (error) {
      console.error('Error merging users:', error);
      toast(error.message || 'Failed to merge users.');
    } finally {
      setActionLoading(null);
    }
  };

  // Get users with same email as a given user
  const getSameEmailUsers = (targetUser) => {
    return allUsers.filter(
      u => u.email?.toLowerCase() === targetUser.email?.toLowerCase() && u.id !== targetUser.id
    );
  };

  // Check for duplicate emails on load
  useEffect(() => {
    const duplicates = findDuplicateEmails();
    setDuplicateUsers(duplicates);
    if (duplicates.length > 0) {
    }
  }, [allUsers]);

  // Filter users
  const filteredUsers = allUsers.filter(u => {
    const matchesSearch = !searchTerm || 
      u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || u.role === filterRole;
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'active' && u.isActive !== false) ||
      (filterStatus === 'inactive' && u.isActive === false);
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Sort filtered users
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    switch (sortBy) {
      case 'name-asc':
        return (a.name || '').localeCompare(b.name || '');
      case 'name-desc':
        return (b.name || '').localeCompare(a.name || '');
      case 'email-asc':
        return (a.email || '').localeCompare(b.email || '');
      case 'email-desc':
        return (b.email || '').localeCompare(a.email || '');
      case 'role':
        return (a.role || 'mentee').localeCompare(b.role || 'mentee');
      case 'conflicts-desc': {
        const aCount = getUserConflicts(a.id).length;
        const bCount = getUserConflicts(b.id).length;
        return bCount - aCount;
      }
      case 'conflicts-asc': {
        const aCount = getUserConflicts(a.id).length;
        const bCount = getUserConflicts(b.id).length;
        return aCount - bCount;
      }
      case 'nda-signed':
        return (agreementSignatures[b.id] ? 1 : 0) - (agreementSignatures[a.id] ? 1 : 0);
      case 'nda-unsigned':
        return (agreementSignatures[a.id] ? 1 : 0) - (agreementSignatures[b.id] ? 1 : 0);
      case 'newest':
        return (b.createdAt || '').localeCompare(a.createdAt || '');
      case 'oldest':
        return (a.createdAt || '').localeCompare(b.createdAt || '');
      default:
        return 0;
    }
  });

  // Role options
  const roleOptions = [
    { value: 'mentee', label: getRoleLabel('mentee') },
    { value: 'mentor', label: getRoleLabel('mentor') },
    { value: 'fly-on-wall', label: getRoleLabel('fly-on-wall') },
    { value: 'omniscient', label: getRoleLabel('omniscient') },
    { value: 'admin', label: getRoleLabel('admin') }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-stone-100">
      {/* Tenant context banner — shown when support is managing a specific tenant */}
      {effectiveTenantName && (
        <div className="bg-amber-500 text-stone-900 text-sm px-4 py-2 flex items-center justify-center gap-2">
          <Shield className="w-4 h-4 flex-shrink-0" />
          <span>Managing tenant: <strong>{effectiveTenantName}</strong> ({effectiveTenantId})</span>
        </div>
      )}
      {/* Header */}
      <header className="bg-white border-b border-stone-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
              aria-label="Back"
            >
              <ArrowLeft className="w-5 h-5 text-stone-600" aria-hidden="true" />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-stone-800">User Management</h1>
              <p className="text-xs text-stone-500">Manage users, roles, and conflict assignments</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                setActionLoading('test-data');
                try {
                  // Create test conflict: Archer's Party Location
                  const testConflict = {
                    id: `conflict-test-${Date.now()}`,
                    title: "Archer's Party Location Disagreement",
                    model: "personal",
                    problemStatement: {
                      who: "Parents (Mom and Dad) planning Archer's birthday party",
                      what: "We originally planned an outdoor party but weather forecast shows rain. Now we can't agree on an alternative indoor location.",
                      where: "Originally planned for backyard, now considering: living room, garage, local community center, or indoor play place",
                      when: "Party is scheduled for this Saturday at 2pm. Need to decide by Thursday to notify guests of any venue change.",
                      how: "Mom wants to book the indoor play place (more fun for kids but expensive). Dad prefers the garage (free but needs cleaning and setup). We've been going back and forth without resolution."
                    },
                    desiredOutcome: "Agree on a party location that works within our budget, provides a fun experience for Archer and friends, and can be confirmed in time to notify guests.",
                    actualOutcome: "",
                    status: "pending-acceptance",
                    statusHistory: [
                      { status: "pending-acceptance", timestamp: new Date().toISOString() }
                    ],
                    createdBy: user.id,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    updatedBy: user.id,
                    responseHours: "9-5",
                    responseWaitTime: 15,
                    responseWindow: 15,
                    termsAcceptance: {
                      required: true,
                      acceptedBy: [user.id],
                      proposedChanges: {},
                      status: "pending"
                    },
                    mentees: [],
                    mentor: null,
                    flyOnWall: null,
                    omniscient: null,
                    comments: [],
                    steps: {
                      identifyDefine: { completed: false, data: {} },
                      communicate: { completed: false, cycles: [], acknowledged: false },
                      exploreAlternatives: { completed: false, alternatives: [] },
                      evaluateSelect: { completed: false, ratings: {}, selected: null },
                      agreeImplement: { completed: false, acknowledged: [] },
                      followUp: { completed: false, ratings: {} }
                    }
                  };
                  
                  await storage.set(`conflict:${testConflict.id}`, testConflict, true);
                  await reloadData();
                  
                  toast(`Test conflict created!\n\nTitle: "${testConflict.title}"\n\nNo participants assigned - you can add participants from this panel.`);
                } catch (error) {
                  console.error('Error creating test data:', error);
                  toast('Failed to create test data. Check console for details.');
                } finally {
                  setActionLoading(null);
                }
              }}
              disabled={actionLoading === 'test-data'}
              className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
              title="Load test conflict data"
            >
              {actionLoading === 'test-data' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileText className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">Load Test Data</span>
            </button>
            <button
              onClick={async () => {
                setActionLoading('repair');
                try {
                  // Find and fix double-stringified conflicts
                  const conflictKeys = await storage.list('conflict:', true);
                  let repaired = 0;
                  let linked = 0;
                  
                  // Get all users for linking
                  const allUsersList = await storage.get(USERS_KEY, true) || [];
                  
                  for (const key of conflictKeys) {
                    try {
                      let conflict = null;
                      let needsSave = false;
                      
                      // Get raw value from window.storage
                      if (window.storage && window.storage.get) {
                        const result = await window.storage.get(key, true);
                        if (result && result.value) {
                          let parsed = JSON.parse(result.value);
                          
                          // Check if it's double-stringified (still a string after first parse)
                          if (typeof parsed === 'string' && (parsed.startsWith('{') || parsed.startsWith('['))) {
                            // It's double-stringified, fix it
                            conflict = JSON.parse(parsed);
                            needsSave = true;
                            repaired++;
                          } else {
                            conflict = parsed;
                          }
                        }
                      }
                      
                      // Link users to mentee entries that still have mentee- IDs
                      if (conflict && conflict.mentees) {
                        for (let i = 0; i < conflict.mentees.length; i++) {
                          const mentee = conflict.mentees[i];
                          
                          // Check if this mentee has a temporary ID and needs linking
                          if (mentee.id?.startsWith('mentee-') && mentee.email) {
                            // Find a user with matching email
                            const matchingUser = allUsersList.find(
                              u => u.email?.toLowerCase() === mentee.email.toLowerCase()
                            );
                            
                            if (matchingUser) {
                              // Check if this user is already linked elsewhere in mentees
                              const alreadyLinked = conflict.mentees.some(
                                m => m.id === matchingUser.id
                              );
                              
                              if (!alreadyLinked) {
                                // Link the user
                                conflict.mentees[i] = {
                                  ...mentee,
                                  id: matchingUser.id,
                                  name: matchingUser.name
                                };
                                needsSave = true;
                                linked++;
                              } else {
                                // User already linked, remove the duplicate mentee- entry
                                conflict.mentees.splice(i, 1);
                                i--; // Adjust index after removal
                                needsSave = true;
                              }
                            }
                          }
                        }
                        
                        // Remove duplicate mentee entries (same email appearing multiple times)
                        const seenEmails = new Set();
                        const uniqueMentees = [];
                        for (const mentee of conflict.mentees) {
                          const email = mentee.email?.toLowerCase();
                          if (email && !seenEmails.has(email)) {
                            seenEmails.add(email);
                            uniqueMentees.push(mentee);
                          } else if (email && seenEmails.has(email)) {
                            needsSave = true;
                          }
                        }
                        if (uniqueMentees.length !== conflict.mentees.length) {
                          conflict.mentees = uniqueMentees;
                        }
                      }
                      
                      // Save if any changes were made
                      if (needsSave && conflict) {
                        conflict.updatedAt = new Date().toISOString();
                        await window.storage.set(key, JSON.stringify(conflict), true);
                      }
                    } catch (e) {
                      console.error(`Error checking/repairing ${key}:`, e);
                    }
                  }
                  
                  // Reload data
                  await reloadData();
                  
                  let message = 'Repair complete!\n\n';
                  if (repaired > 0) {
                    message += `• ${repaired} conflict(s) had corrupted data and were fixed\n`;
                  }
                  if (linked > 0) {
                    message += `• ${linked} user(s) were linked to their conflict invitations\n`;
                  }
                  if (repaired === 0 && linked === 0) {
                    message += 'No issues found. All data is OK.';
                  }
                  
                  toast(message);
                } catch (error) {
                  console.error('Repair error:', error);
                  toast('Repair failed. Check console for details.');
                } finally {
                  setActionLoading(null);
                }
              }}
              disabled={actionLoading === 'repair'}
              className="flex items-center gap-2 bg-green-700 hover:bg-green-800 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
              title="Repair corrupted conflict data and link users"
            >
              {actionLoading === 'repair' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">Repair Data</span>
            </button>
            <button
              onClick={() => setShowCreateUserModal(true)}
              className="flex items-center gap-2 bg-amber-700 hover:bg-amber-800 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Create User</span>
            </button>
            <button
              onClick={() => { resetBulkUpload(); setShowBulkUploadModal(true); }}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              <Upload className="w-4 h-4" />
              <span className="hidden sm:inline">Bulk Upload</span>
            </button>
            <button
              onClick={reloadData}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-5 h-5 text-stone-600 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-7 gap-3 mb-6">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-200">
            <div className="flex items-center gap-2 text-stone-600 mb-1">
              <Users className="w-4 h-4" />
              <span className="text-xs font-medium">Total Users</span>
            </div>
            <div className="text-2xl font-bold text-stone-800">{allUsers.length}</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-200">
            <div className="flex items-center gap-2 text-green-700 mb-1">
              <CheckCircle className="w-4 h-4" />
              <span className="text-xs font-medium">Active</span>
            </div>
            <div className="text-2xl font-bold text-green-700">
              {allUsers.filter(u => u.isActive !== false).length}
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-200">
            <div className="flex items-center gap-2 text-red-600 mb-1">
              <XCircle className="w-4 h-4" />
              <span className="text-xs font-medium">Inactive</span>
            </div>
            <div className="text-2xl font-bold text-red-600">
              {allUsers.filter(u => u.isActive === false).length}
            </div>
          </div>
          <div className={`rounded-xl p-4 shadow-sm border ${Object.keys(agreementSignatures).length < allUsers.length ? 'bg-green-50 border-green-200' : 'bg-white border-stone-200'}`}>
            <div className="flex items-center gap-2 text-green-700 mb-1">
              <FileText className="w-4 h-4" />
              <span className="text-xs font-medium">Agreement Signed</span>
            </div>
            <div className="text-2xl font-bold text-green-700">
              {Object.keys(agreementSignatures).length}<span className="text-sm font-normal text-stone-500">/{allUsers.length}</span>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-200">
            <div className="flex items-center gap-2 text-amber-700 mb-1">
              <AlertCircle className="w-4 h-4" />
              <span className="text-xs font-medium">Admins</span>
            </div>
            <div className="text-2xl font-bold text-amber-700">
              {allUsers.filter(u => u.role === 'admin').length}
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-200">
            <div className="flex items-center gap-2 text-blue-600 mb-1">
              <FileText className="w-4 h-4" />
              <span className="text-xs font-medium">Conflicts</span>
            </div>
            <div className="text-2xl font-bold text-blue-600">{allConflicts.length}</div>
          </div>
          <div className={`rounded-xl p-4 shadow-sm border ${duplicateUsers.length > 0 ? 'bg-orange-50 border-orange-200' : 'bg-white border-stone-200'}`}>
            <div className={`flex items-center gap-2 mb-1 ${duplicateUsers.length > 0 ? 'text-orange-700' : 'text-stone-500'}`}>
              <Users className="w-4 h-4" />
              <span className="text-xs font-medium">Duplicates</span>
            </div>
            <div className={`text-2xl font-bold ${duplicateUsers.length > 0 ? 'text-orange-700' : 'text-stone-500'}`}>
              {duplicateUsers.length}
            </div>
          </div>
        </div>

        {/* Duplicate Users Warning */}
        {duplicateUsers.length > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-orange-800 mb-1">Duplicate Email Addresses Detected</h3>
                <p className="text-sm text-orange-700 mb-3">
                  The following email addresses have multiple user accounts. Consider merging them to consolidate conflict history.
                </p>
                <div className="space-y-2">
                  {duplicateUsers.map(({ email, users }) => (
                    <div key={email} className="bg-white rounded-lg p-3 border border-orange-200">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                          <span className="font-medium text-stone-800">{email}</span>
                          <span className="text-sm text-stone-500 ml-2">({users.length} accounts)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-xs text-stone-500">
                            {users.map(u => u.name).join(', ')}
                          </div>
                          <button
                            onClick={() => openMergeModal(users[0])}
                            className="px-3 py-1.5 bg-orange-700 hover:bg-orange-800 text-white text-xs rounded-lg font-medium transition-colors"
                          >
                            Merge Users
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Team Mood Overview */}
        <TeamMoodOverview allUsers={allUsers} />

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-200 mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-500" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
            </div>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="px-4 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            >
              <option value="all">All Roles</option>
              {roleOptions.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            >
              <option value="name-asc">Name A→Z</option>
              <option value="name-desc">Name Z→A</option>
              <option value="email-asc">Email A→Z</option>
              <option value="email-desc">Email Z→A</option>
              <option value="role">Role</option>
              <option value="conflicts-desc">Most Conflicts</option>
              <option value="conflicts-asc">Least Conflicts</option>
              <option value="nda-signed">Agreement Signed First</option>
              <option value="nda-unsigned">Agreement Unsigned First</option>
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
            </select>
          </div>
          {(searchTerm || filterRole !== 'all' || filterStatus !== 'all') && (
            <div className="mt-2 flex items-center justify-between">
              <p className="text-xs text-stone-500">
                Showing {sortedUsers.length} of {allUsers.length} users
              </p>
              <button
                onClick={() => { setSearchTerm(''); setFilterRole('all'); setFilterStatus('all'); }}
                className="text-xs text-amber-700 hover:text-amber-800 font-medium"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>

        {/* Users List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-amber-700 animate-spin" />
          </div>
        ) : sortedUsers.length === 0 ? (
          <div className="bg-white rounded-xl p-8 shadow-sm border border-stone-200 text-center">
            <Users className="w-12 h-12 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500">No users found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {sortedUsers.map(targetUser => {
              const userConflicts = getUserConflicts(targetUser.id);
              const isExpanded = selectedUser === targetUser.id;
              const isActive = targetUser.isActive !== false;
              
              return (
                <div 
                  key={targetUser.id}
                  className={`bg-white rounded-xl shadow-sm border ${isActive ? 'border-stone-200' : 'border-red-200 bg-red-50'}`}
                >
                  {/* User Row */}
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-stone-900 font-medium ${isActive ? 'bg-amber-500' : 'bg-stone-400'}`}>
                          {targetUser.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-stone-800 truncate">{targetUser.name}</span>
                            {targetUser.id === user.id && (
                              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">You</span>
                            )}
                            {!isActive && (
                              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Inactive</span>
                            )}
                            {userBillingMap[targetUser.id]?.plan && (
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                userBillingMap[targetUser.id].plan === 'enterprise' ? 'bg-purple-100 text-purple-700' :
                                userBillingMap[targetUser.id].plan === 'professional' ? 'bg-blue-100 text-blue-700' :
                                userBillingMap[targetUser.id].plan === 'personal-plus' ? 'bg-green-100 text-green-700' :
                                userBillingMap[targetUser.id].plan === 'personal' ? 'bg-stone-100 text-stone-600' :
                                'bg-stone-100 text-stone-600'
                              }`}>
                                {userBillingMap[targetUser.id].plan === 'personal-plus' ? 'Personal+' : userBillingMap[targetUser.id].plan.charAt(0).toUpperCase() + userBillingMap[targetUser.id].plan.slice(1)}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-stone-500 truncate">{targetUser.email}</div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 ml-4">
                        {/* Role Dropdown */}
                        <select
                          value={targetUser.role || 'mentee'}
                          onChange={(e) => changeUserRole(targetUser, e.target.value)}
                          disabled={actionLoading === targetUser.id}
                          className="px-3 py-1.5 text-sm border border-stone-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent disabled:opacity-50"
                        >
                          {roleOptions.map(r => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                        
                        {/* Toggle Active */}
                        <button
                          onClick={() => toggleUserStatus(targetUser)}
                          disabled={actionLoading === targetUser.id || targetUser.id === user.id}
                          className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
                            isActive
                              ? 'hover:bg-amber-50 text-amber-700'
                              : 'hover:bg-green-50 text-green-700'
                          }`}
                          title={isActive ? 'Deactivate User' : 'Activate User'}
                        >
                          {actionLoading === targetUser.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : isActive ? (
                            <ShieldOff className="w-4 h-4" />
                          ) : (
                            <Shield className="w-4 h-4" />
                          )}
                        </button>
                        
                        {/* Merge Users - show if duplicate emails exist */}
                        {getSameEmailUsers(targetUser).length > 0 && (
                          <button
                            onClick={() => openMergeModal(targetUser)}
                            disabled={actionLoading === 'merge-users'}
                            className="p-2 rounded-lg transition-colors hover:bg-orange-50 text-orange-700"
                            title={`Merge with ${getSameEmailUsers(targetUser).length} duplicate account(s)`}
                          >
                            <Users className="w-4 h-4" />
                          </button>
                        )}
                        
                        {/* Delete User */}
                        {targetUser.id !== user.id && (
                          <button
                            onClick={() => deleteUser(targetUser)}
                            disabled={actionLoading === `delete-${targetUser.id}`}
                            className="p-2 rounded-lg transition-colors hover:bg-red-50 text-red-600 disabled:opacity-50"
                            title="Delete User Permanently"
                          >
                            {actionLoading === `delete-${targetUser.id}` ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        )}
                        
                        {/* Expand/Collapse */}
                        <button
                          onClick={() => setSelectedUser(isExpanded ? null : targetUser.id)}
                          className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
                        >
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-stone-600" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-stone-600" />
                          )}
                        </button>
                      </div>
                    </div>
                    
                    {/* Quick Stats */}
                    <div className="flex gap-4 mt-3 text-xs text-stone-500 flex-wrap">
                      <span>{userConflicts.length} conflict{userConflicts.length !== 1 ? 's' : ''}</span>
                      <span>{allRelationships.filter(r => r.initiatorId === targetUser.id || r.receiverId === targetUser.id || r.mentorId === targetUser.id || r.participantId === targetUser.id).length} relationship{allRelationships.filter(r => r.initiatorId === targetUser.id || r.receiverId === targetUser.id || r.mentorId === targetUser.id || r.participantId === targetUser.id).length !== 1 ? 's' : ''}</span>
                      {freeToolsCounts[targetUser.id] && (freeToolsCounts[targetUser.id].votes > 0 || freeToolsCounts[targetUser.id].siyw > 0 || freeToolsCounts[targetUser.id].mood > 0) && (
                        <span className="text-purple-600">
                          {[
                            freeToolsCounts[targetUser.id].votes > 0 && `${freeToolsCounts[targetUser.id].votes} vote${freeToolsCounts[targetUser.id].votes !== 1 ? 's' : ''}`,
                            freeToolsCounts[targetUser.id].siyw > 0 && `${freeToolsCounts[targetUser.id].siyw} SIYW`,
                            freeToolsCounts[targetUser.id].mood > 0 && `${freeToolsCounts[targetUser.id].mood} mood${freeToolsCounts[targetUser.id].mood !== 1 ? 's' : ''}`,
                          ].filter(Boolean).join(' · ')}
                        </span>
                      )}
                      <span>Joined {new Date(targetUser.createdAt || Date.now()).toLocaleDateString()}</span>
                      {agreementSignatures[targetUser.id] && (
                        <span className="text-green-700">Agreement signed {new Date(agreementSignatures[targetUser.id].signedAt).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Expanded: User's Conflicts */}
                  {isExpanded && (
                    <div className="border-t border-stone-200 p-4 bg-stone-50">
                      <h4 className="font-medium text-stone-700 mb-3 flex items-center gap-1.5">
                        Conflicts in Your Hierarchy
                        <span className="relative group">
                          <Info size={14} className="text-stone-500 cursor-help" />
                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-stone-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                            Conflicts visible through this user's mentor-mentee chain
                          </span>
                        </span>
                      </h4>
                      {userConflicts.length === 0 ? (
                        <p className="text-sm text-stone-500">No conflicts in your hierarchy</p>
                      ) : (
                        <div className="space-y-3">
                          {userConflicts.map(conflict => {
                            const isMentee = conflict.mentees?.some(m => m.id === targetUser.id);
                            const isFlyOnWall = conflict.flyOnWall?.id === targetUser.id;
                            const isOmniscient = conflict.omniscient?.id === targetUser.id;
                            const isCreator = conflict.createdBy === targetUser.id;
                            
                            const roles = [];
                            if (isCreator) roles.push('Creator');
                            if (isMentee) roles.push('Participant');
                            if (isFlyOnWall) roles.push('Observer');
                            if (isOmniscient) roles.push('Global');
                            
                            return (
                              <div 
                                key={conflict.id}
                                className="bg-white rounded-lg p-3 border border-stone-200"
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0 flex-1">
                                    <div className="font-medium text-stone-800 truncate">{conflict.title}</div>
                                    <div className="text-xs text-stone-500 mt-1">
                                      {roles.join(' • ')} | {conflict.status || 'In Progress'}
                                    </div>
                                  </div>
                                  <div className="flex gap-1">
                                    {/* Reassign Button */}
                                    {(isMentee || isFlyOnWall || isOmniscient) && !isCreator && (
                                      <button
                                        onClick={() => openReassignModal(
                                          conflict,
                                          isMentee ? 'mentee' : isFlyOnWall ? 'flyOnWall' : 'omniscient',
                                          targetUser.id
                                        )}
                                        className="p-1.5 hover:bg-amber-50 text-amber-700 rounded transition-colors"
                                        title="Reassign"
                                      >
                                        <RefreshCw className="w-4 h-4" />
                                      </button>
                                    )}
                                    {/* Remove Button */}
                                    {(isMentee || isFlyOnWall || isOmniscient) && !isCreator && (
                                      <button
                                        onClick={() => removeFromConflict(
                                          conflict,
                                          isMentee ? 'mentee' : isFlyOnWall ? 'flyOnWall' : 'omniscient',
                                          targetUser.id
                                        )}
                                        disabled={actionLoading === `${conflict.id}-${isMentee ? 'mentee' : isFlyOnWall ? 'flyOnWall' : 'omniscient'}`}
                                        className="p-1.5 hover:bg-red-50 text-red-600 rounded transition-colors disabled:opacity-50"
                                        title="Remove from Conflict"
                                      >
                                        {actionLoading === `${conflict.id}-${isMentee ? 'mentee' : isFlyOnWall ? 'flyOnWall' : 'omniscient'}` ? (
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                          <Trash2 className="w-4 h-4" />
                                        )}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* Add to Conflict Button */}
                      <button
                        onClick={() => {
                          setAddToConflictUser(targetUser);
                          setShowAddToConflictModal(true);
                        }}
                        className="mt-4 flex items-center gap-2 text-sm text-amber-700 hover:text-amber-800 font-medium"
                      >
                        <Plus className="w-4 h-4" />
                        Add to Conflict
                      </button>

                      {/* Email Notification Preferences */}
                      <div className="mt-6 bg-white rounded-lg p-4 border border-stone-200">
                        <div className="flex items-center gap-2 mb-1">
                          <Mail className="w-4 h-4 text-amber-700" />
                          <h4 className="font-medium text-stone-700">Email Notification Preferences</h4>
                        </div>
                        <p className="text-xs text-stone-500 mb-3">Fallback emails sent only when a push notification can't be delivered.</p>
                        <div className="space-y-2">
                          {EMAIL_NOTIF_CATEGORIES.map(({ key, label }) => {
                            const enabled = targetUser[key] !== false;
                            return (
                              <div key={key} className="flex items-center justify-between">
                                <span className="text-sm text-stone-700">{label}</span>
                                <button
                                  onClick={() => toggleEmailPref(targetUser, key)}
                                  className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                                    enabled
                                      ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                                      : 'bg-stone-200 text-stone-600 hover:bg-stone-300'
                                  }`}
                                >
                                  {enabled ? 'On' : 'Off'}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Relationships Section — P7: view, inactivate, add new */}
                      {(() => {
                        const userRels = allRelationships.filter(r =>
                          r.initiatorId === targetUser.id || r.receiverId === targetUser.id ||
                          r.mentorId === targetUser.id || r.participantId === targetUser.id
                        );

                        const typeLabels = {
                          'mentor-participant': 'Mentor ↔ Participant',
                          'fotw-mentor': 'Observer ↔ Mentor',
                          'omni-fotw': 'Global ↔ Observer',
                        };
                        const statusColors = {
                          'active': 'bg-green-100 text-green-700',
                          'pending': 'bg-amber-100 text-amber-700',
                          'declined': 'bg-red-100 text-red-700',
                          'inactive': 'bg-stone-100 text-stone-600',
                          'revoked': 'bg-stone-100 text-stone-600',
                        };

                        const handleInactivateRel = async (relId) => {
                          if (!window.confirm('Inactivate this relationship? The history will be preserved.')) return;
                          try {
                            const freshRels = await loadAllRelationships();
                            const rel = freshRels.find(r => r.id === relId);
                            if (rel) {
                              // Stamp affected conflicts with access snapshot before revoking
                              try {
                                const revokedUserId = rel.initiatorId === selectedUser.id ? rel.receiverId : rel.initiatorId;
                                const revokedUserName = rel.initiatorId === selectedUser.id ? rel.receiverName : rel.initiatorName;
                                const revokedUserEmail = rel.initiatorId === selectedUser.id ? rel.receiverEmail : rel.initiatorEmail;
                                const revokedRole = rel.initiatorId === selectedUser.id ? rel.receiverRole : rel.initiatorRole;
                                const allConflictData = await storage.list('conflict:', true);
                                const affected = (allConflictData || []).filter(c =>
                                  deriveConflictAccess(c, { id: revokedUserId, email: revokedUserEmail, role: revokedRole }, freshRels).canRead
                                );
                                for (const c of affected) {
                                  const accessBefore = computeRelationalAccessSnapshot(c, freshRels);
                                  const details = `Relationship revoked: ${selectedUser.name} ↔ ${revokedUserName} (${revokedUserEmail || 'no email'}). Access before: ${accessBefore.length > 0 ? accessBefore.map(a => `${a.name} (${a.email || 'no email'}): ${a.accessPath}`).join('; ') : 'None'}`;
                                  addAuditEntry(c, user.id, user.name, 'Relationship revoked: access snapshot', details, user.email);
                                  await storage.set(`conflict:${c.id}`, c, true);
                                }
                              } catch (snapErr) {
                                console.warn('[UserMgmt] Relationship access snapshot failed:', snapErr);
                              }

                              rel.status = 'inactive';
                              rel.revokedAt = new Date().toISOString();
                              rel.revokedBy = user.id;
                              const savedRels = await saveRelationship(rel, freshRels, rel.tenantId || effectiveTenantId);
                              setAllRelationships(savedRels);
                              refreshRelationships(); // sync session-level context
                              toast('Relationship inactivated.');
                            }
                          } catch (e) {
                            console.error('Error inactivating relationship:', e);
                            toast('Failed to inactivate. Please try again.');
                          }
                        };

                        const handleAddRelationship = async () => {
                          const email = addRelEmail.trim().toLowerCase();
                          if (!email || !email.includes('@')) { toast('Please enter a valid email.'); return; }
                          setAddRelLoading(true);
                          try {
                            // Look up the other user across all tenants
                            let otherUser = allUsers.find(u => u.email?.toLowerCase() === email);
                            if (!otherUser) {
                              const allTenants = await loadVisibleTenants();
                              for (const tenant of (Array.isArray(allTenants) ? allTenants : [])) {
                                if (tenant.id === effectiveTenantId) continue;
                                const tenantUsers = await storage.get(getTenantUsersKey(tenant.id), true) || [];
                                const found = (Array.isArray(tenantUsers) ? tenantUsers : []).find(u => u.email?.toLowerCase() === email);
                                if (found) { otherUser = found; break; }
                              }
                            }
                            if (!otherUser) { toast('No account found with that email.'); setAddRelLoading(false); return; }
                            if (otherUser.id === targetUser.id) { toast("Can't connect a user to themselves."); setAddRelLoading(false); return; }

                            // Determine roles based on addRelType and addRelRole
                            let initiatorId, initiatorName, initiatorEmail, initiatorRole;
                            let receiverId, receiverName, receiverEmail, receiverRole;
                            let relType = addRelType;

                            if (relType === 'mentor-participant') {
                              if (addRelRole === 'mentor') {
                                // targetUser is the participant, otherUser is the mentor
                                initiatorId = otherUser.id; initiatorName = otherUser.name; initiatorEmail = otherUser.email; initiatorRole = 'mentor';
                                receiverId = targetUser.id; receiverName = targetUser.name; receiverEmail = targetUser.email; receiverRole = 'participant';
                              } else {
                                // targetUser is the mentor, otherUser is the participant
                                initiatorId = targetUser.id; initiatorName = targetUser.name; initiatorEmail = targetUser.email; initiatorRole = 'mentor';
                                receiverId = otherUser.id; receiverName = otherUser.name; receiverEmail = otherUser.email; receiverRole = 'participant';
                              }
                            } else if (relType === 'fotw-mentor') {
                              if (addRelRole === 'fly-on-wall') {
                                // targetUser is the mentor, otherUser is the observer
                                initiatorId = otherUser.id; initiatorName = otherUser.name; initiatorEmail = otherUser.email; initiatorRole = 'fly-on-wall';
                                receiverId = targetUser.id; receiverName = targetUser.name; receiverEmail = targetUser.email; receiverRole = 'mentor';
                              } else {
                                // targetUser is the observer, otherUser is the mentor
                                initiatorId = targetUser.id; initiatorName = targetUser.name; initiatorEmail = targetUser.email; initiatorRole = 'fly-on-wall';
                                receiverId = otherUser.id; receiverName = otherUser.name; receiverEmail = otherUser.email; receiverRole = 'mentor';
                              }
                            } else if (relType === 'omni-fotw') {
                              if (addRelRole === 'omniscient') {
                                // targetUser is the observer, otherUser is the global
                                initiatorId = otherUser.id; initiatorName = otherUser.name; initiatorEmail = otherUser.email; initiatorRole = 'omniscient';
                                receiverId = targetUser.id; receiverName = targetUser.name; receiverEmail = targetUser.email; receiverRole = 'fly-on-wall';
                              } else {
                                // targetUser is the global, otherUser is the observer
                                initiatorId = targetUser.id; initiatorName = targetUser.name; initiatorEmail = targetUser.email; initiatorRole = 'omniscient';
                                receiverId = otherUser.id; receiverName = otherUser.name; receiverEmail = otherUser.email; receiverRole = 'fly-on-wall';
                              }
                            }

                            // Check for existing active/pending relationship of this type between these users
                            const freshRels = await loadAllRelationships();
                            const existing = freshRels.find(r =>
                              r.type === relType &&
                              (r.status === 'active' || r.status === 'pending') &&
                              ((r.initiatorId === initiatorId && r.receiverId === receiverId) ||
                               (r.initiatorId === receiverId && r.receiverId === initiatorId))
                            );
                            if (existing) {
                              toast(`An ${existing.status} ${typeLabels[relType]} relationship already exists between these users.`);
                              setAddRelLoading(false);
                              return;
                            }

                            const newRel = {
                              id: `rel-${Date.now()}`,
                              type: relType,
                              initiatorId, initiatorName, initiatorEmail, initiatorRole,
                              receiverId, receiverName, receiverEmail, receiverRole,
                              status: 'active', // Admin/support-created relationships are immediately active
                              invitedAt: new Date().toISOString(),
                              acceptedAt: new Date().toISOString(),
                              createdVia: 'admin',
                              createdBy: user.id,
                              tenantId: effectiveTenantId,
                            };
                            // Add legacy fields for mentor-participant
                            if (relType === 'mentor-participant') {
                              newRel.mentorId = initiatorRole === 'mentor' ? initiatorId : receiverId;
                              newRel.participantId = initiatorRole === 'participant' ? initiatorId : receiverId;
                            }

                            const savedRels = await saveRelationship(newRel, freshRels, effectiveTenantId);
                            setAllRelationships(savedRels);
                            refreshRelationships(); // sync session-level context
                            toast(`Connection created: ${targetUser.name} ↔ ${otherUser.name}`);
                            setAddRelForUser(null);
                            setAddRelEmail('');
                          } catch (e) {
                            console.error('Error adding relationship:', e);
                            toast('Failed to create connection. Please try again.');
                          }
                          setAddRelLoading(false);
                        };

                        // Determine default role options based on target user's role
                        const roleTypeOptions = [
                          { type: 'mentor-participant', label: 'Mentor ↔ Participant', roles: [
                            { value: 'mentor', label: 'Other user is the Mentor' },
                            { value: 'participant', label: 'Other user is the Participant' },
                          ]},
                          { type: 'fotw-mentor', label: 'Observer ↔ Mentor', roles: [
                            { value: 'fly-on-wall', label: 'Other user is the Observer' },
                            { value: 'mentor', label: 'Other user is the Mentor' },
                          ]},
                          { type: 'omni-fotw', label: 'Global ↔ Observer', roles: [
                            { value: 'omniscient', label: 'Other user is the Global' },
                            { value: 'fly-on-wall', label: 'Other user is the Observer' },
                          ]},
                        ];
                        const currentTypeOption = roleTypeOptions.find(o => o.type === addRelType);

                        return (
                          <div className="mt-4 pt-4 border-t border-stone-200">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-medium text-stone-700">Relationships ({userRels.length})</h4>
                              {addRelForUser !== targetUser.id && (
                                <button
                                  onClick={() => { setAddRelForUser(targetUser.id); setAddRelEmail(''); setAddRelType('mentor-participant'); setAddRelRole('mentor'); }}
                                  className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 font-medium px-2 py-1 rounded hover:bg-purple-50 transition-colors"
                                >
                                  <Plus className="w-3.5 h-3.5" /> Add Connection
                                </button>
                              )}
                            </div>

                            {/* Add Connection Form */}
                            {addRelForUser === targetUser.id && (
                              <div className="mb-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-xs font-semibold text-purple-800">New Connection for {targetUser.name}</span>
                                  <button onClick={() => setAddRelForUser(null)} className="p-0.5 hover:bg-purple-100 rounded">
                                    <X className="w-3.5 h-3.5 text-purple-500" />
                                  </button>
                                </div>
                                <div className="space-y-2">
                                  <select
                                    value={addRelType}
                                    onChange={e => { setAddRelType(e.target.value); const opt = roleTypeOptions.find(o => o.type === e.target.value); if (opt) setAddRelRole(opt.roles[0].value); }}
                                    className="w-full text-xs border border-purple-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                                  >
                                    {roleTypeOptions.map(o => (
                                      <option key={o.type} value={o.type}>{o.label}</option>
                                    ))}
                                  </select>
                                  <select
                                    value={addRelRole}
                                    onChange={e => setAddRelRole(e.target.value)}
                                    className="w-full text-xs border border-purple-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                                  >
                                    {currentTypeOption && currentTypeOption.roles.map(r => (
                                      <option key={r.value} value={r.value}>{r.label}</option>
                                    ))}
                                  </select>
                                  <div className="flex gap-2">
                                    <input
                                      type="email"
                                      value={addRelEmail}
                                      onChange={e => setAddRelEmail(e.target.value)}
                                      placeholder="Other user's email"
                                      className="flex-1 text-xs border border-purple-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-purple-400"
                                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddRelationship(); } }}
                                    />
                                    <button
                                      onClick={handleAddRelationship}
                                      disabled={addRelLoading || !addRelEmail.trim()}
                                      className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:bg-stone-300 text-white text-xs rounded-lg font-medium transition-colors flex items-center gap-1"
                                    >
                                      {addRelLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                                      Create
                                    </button>
                                  </div>
                                  <p className="text-xs text-purple-600">Admin-created connections are immediately active (no approval needed).</p>
                                </div>
                              </div>
                            )}

                            {userRels.length === 0 && addRelForUser !== targetUser.id ? (
                              <p className="text-sm text-stone-500">No relationships established</p>
                            ) : (
                              <div className="space-y-2">
                                {userRels.map(rel => {
                                  const isInitiator = rel.initiatorId === targetUser.id;
                                  const otherName = isInitiator ? rel.receiverName : rel.initiatorName;
                                  const otherEmail = isInitiator ? rel.receiverEmail : rel.initiatorEmail;
                                  const otherRole = isInitiator ? rel.receiverRole : rel.initiatorRole;
                                  const myRoleInRel = isInitiator ? rel.initiatorRole : rel.receiverRole;
                                  const isActive = rel.status === 'active';

                                  return (
                                    <div key={rel.id} className="bg-white rounded-lg p-3 border border-stone-200 flex items-center justify-between group">
                                      <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">{myRoleInRel}</span>
                                          <span className="text-stone-500">→</span>
                                          <span className="text-sm font-medium text-stone-800 truncate">{otherName || otherEmail}</span>
                                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{otherRole}</span>
                                          <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[rel.status] || 'bg-stone-100 text-stone-600'}`}>{(rel.status || '').replace(/\b\w/g, c => c.toUpperCase())}</span>
                                        </div>
                                        <div className="text-xs text-stone-500 mt-1">
                                          {typeLabels[rel.type] || rel.type} • {rel.invitedAt ? new Date(rel.invitedAt).toLocaleDateString() : ''}
                                          {rel.createdVia === 'migration' && ' • migrated'}
                                          {rel.createdVia === 'admin' && ' • created by admin'}
                                        </div>
                                      </div>
                                      {isActive && (
                                        <button
                                          onClick={() => handleInactivateRel(rel.id)}
                                          className="text-xs text-stone-500 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 px-2 py-1 rounded hover:bg-red-50 ml-2"
                                          title="Inactivate this connection"
                                        >
                                          Inactivate
                                        </button>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* ── Free Tools Section ── */}
                      <div className="mt-4 pt-4 border-t border-stone-200">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-stone-700">Free Tools</h4>
                          <button
                            onClick={async () => {
                              if (freeToolsExpanded === targetUser.id) {
                                setFreeToolsExpanded(null);
                              } else {
                                setFreeToolsExpanded(targetUser.id);
                                await loadFreeToolsData(targetUser.id);
                              }
                            }}
                            className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 font-medium px-2 py-1 rounded hover:bg-purple-50 transition-colors"
                          >
                            {freeToolsExpanded === targetUser.id ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                            {freeToolsExpanded === targetUser.id ? 'Collapse' : 'Expand'}
                          </button>
                        </div>

                        {freeToolsExpanded === targetUser.id && (
                          <div>
                            {freeToolsLoading ? (
                              <div className="flex items-center gap-2 text-stone-500 text-sm py-4 justify-center">
                                <Loader2 className="w-4 h-4 animate-spin" /> Loading free tools data…
                              </div>
                            ) : (
                              <>
                                {/* Tab navigation */}
                                <div className="flex gap-1 mb-3">
                                  {[
                                    { key: 'votes', label: 'Votes', count: Object.keys(freeToolsData.votes || {}).length },
                                    { key: 'siyw', label: 'SIYW Sessions', count: Object.keys(freeToolsData.siyw || {}).length },
                                    { key: 'mood', label: 'Mood Check-ins', count: Object.keys(freeToolsData.mood || {}).length },
                                  ].map(tab => (
                                    <button
                                      key={tab.key}
                                      onClick={() => setFreeToolsSection(tab.key)}
                                      className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${
                                        freeToolsSection === tab.key
                                          ? 'bg-purple-100 text-purple-700'
                                          : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                                      }`}
                                    >
                                      {tab.label} ({tab.count})
                                    </button>
                                  ))}
                                </div>

                                {/* ── Votes ── */}
                                {freeToolsSection === 'votes' && (() => {
                                  const votes = freeToolsData.votes || {};
                                  const voteEntries = Object.entries(votes).sort((a, b) => (b[1].createdAt || '').localeCompare(a[1].createdAt || ''));
                                  if (voteEntries.length === 0) return <p className="text-xs text-stone-500 italic">No votes found.</p>;
                                  return (
                                    <div className="space-y-2 max-h-64 overflow-y-auto">
                                      {voteEntries.map(([voteId, vote]) => (
                                        <div key={voteId} className="bg-white rounded-lg p-3 border border-stone-200 group">
                                          <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0 flex-1">
                                              {editingFreeItem?.type === 'votes' && editingFreeItem?.id === voteId && editingFreeItem?.field === 'subject' ? (
                                                <div className="flex items-center gap-1">
                                                  <input
                                                    className="text-sm font-medium border border-purple-300 rounded px-2 py-0.5 flex-1"
                                                    value={editingFreeItem.value}
                                                    onChange={e => setEditingFreeItem({ ...editingFreeItem, value: e.target.value })}
                                                    autoFocus
                                                  />
                                                  <button onClick={() => handleSaveFreeItem(targetUser.id, 'votes', voteId, 'subject', editingFreeItem.value)} className="text-xs text-green-700 hover:text-green-800 px-1">Save</button>
                                                  <button onClick={() => setEditingFreeItem(null)} className="text-xs text-stone-500 hover:text-stone-600 px-1">Cancel</button>
                                                </div>
                                              ) : (
                                                <div className="flex items-center gap-1">
                                                  <span className="text-sm font-medium text-stone-800 truncate">{vote.subject || '(no subject)'}</span>
                                                  <button onClick={() => setEditingFreeItem({ type: 'votes', id: voteId, field: 'subject', value: vote.subject || '' })} className="opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <Pencil className="w-3 h-3 text-stone-500 hover:text-purple-600" />
                                                  </button>
                                                </div>
                                              )}
                                              <div className="text-xs text-stone-500 mt-0.5">
                                                <span className={`inline-flex px-1.5 py-0.5 rounded-full text-xs font-medium mr-1 ${vote.status === 'closed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                                  {vote.status === 'closed' ? 'Closed' : 'Open'}
                                                </span>
                                                {vote.options?.length || 0} options • {Object.keys(vote.ratings || {}).length} votes
                                                {vote.createdAt && ` • ${new Date(vote.createdAt).toLocaleDateString()}`}
                                                {vote.shareCode && ` • Code: ${vote.shareCode}`}
                                              </div>
                                            </div>
                                            <button
                                              onClick={() => handleDeleteFreeItem(targetUser.id, 'votes', voteId)}
                                              disabled={actionLoading === `free-votes-${voteId}`}
                                              className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-50 text-red-700 hover:text-red-800 rounded flex-shrink-0"
                                              title="Delete vote"
                                            >
                                              {actionLoading === `free-votes-${voteId}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  );
                                })()}

                                {/* ── SIYW Sessions ── */}
                                {freeToolsSection === 'siyw' && (() => {
                                  const siyw = freeToolsData.siyw || {};
                                  const siywEntries = Object.entries(siyw).sort((a, b) => (b[1].timestamp || '').localeCompare(a[1].timestamp || ''));
                                  if (siywEntries.length === 0) return <p className="text-xs text-stone-500 italic">No SIYW sessions found.</p>;
                                  return (
                                    <div className="space-y-2 max-h-64 overflow-y-auto">
                                      {siywEntries.map(([sessionId, session]) => (
                                        <div key={sessionId} className="bg-white rounded-lg p-3 border border-stone-200 group">
                                          <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0 flex-1">
                                              <div className="flex items-center gap-2 mb-1">
                                                <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${session.mode === 'feelings' ? 'bg-pink-100 text-pink-700' : 'bg-blue-100 text-blue-700'}`}>
                                                  {session.mode === 'feelings' ? 'Express Feelings' : 'Define Conflict'}
                                                </span>
                                                <span className="text-xs text-stone-500">
                                                  {session.timestamp ? new Date(session.timestamp).toLocaleString() : ''}
                                                </span>
                                              </div>
                                              {session.extracted && (
                                                <div className="text-xs text-stone-600 space-y-0.5">
                                                  {session.extracted.what && (
                                                    <div className="flex items-start gap-1">
                                                      <span className="text-stone-500 flex-shrink-0">What:</span>
                                                      {editingFreeItem?.type === 'siyw' && editingFreeItem?.id === sessionId && editingFreeItem?.field === 'extracted.what' ? (
                                                        <span className="flex items-center gap-1 flex-1">
                                                          <input className="text-xs border border-purple-300 rounded px-1 py-0.5 flex-1" value={editingFreeItem.value} onChange={e => setEditingFreeItem({ ...editingFreeItem, value: e.target.value })} autoFocus />
                                                          <button onClick={() => handleSaveFreeItem(targetUser.id, 'siyw', sessionId, 'extracted.what', editingFreeItem.value)} className="text-xs text-green-700 px-1">Save</button>
                                                          <button onClick={() => setEditingFreeItem(null)} className="text-xs text-stone-500 px-1">Cancel</button>
                                                        </span>
                                                      ) : (
                                                        <button type="button" className="truncate min-w-0 text-left cursor-pointer hover:text-purple-600" onClick={() => setEditingFreeItem({ type: 'siyw', id: sessionId, field: 'extracted.what', value: session.extracted.what })}>{session.extracted.what}</button>
                                                      )}
                                                    </div>
                                                  )}
                                                  {session.extracted.who && <div><span className="text-stone-500">Who:</span> {session.extracted.who}</div>}
                                                  {session.extracted.feelings && <div><span className="text-stone-500">Feelings:</span> {session.extracted.feelings}</div>}
                                                </div>
                                              )}
                                            </div>
                                            <button
                                              onClick={() => handleDeleteFreeItem(targetUser.id, 'siyw', sessionId)}
                                              disabled={actionLoading === `free-siyw-${sessionId}`}
                                              className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-50 text-red-700 hover:text-red-800 rounded flex-shrink-0"
                                              title="Delete session"
                                            >
                                              {actionLoading === `free-siyw-${sessionId}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  );
                                })()}

                                {/* ── Mood Check-ins ── */}
                                {freeToolsSection === 'mood' && (() => {
                                  const mood = freeToolsData.mood || {};
                                  const moodEntries = Object.entries(mood).sort((a, b) => b[0].localeCompare(a[0]));
                                  if (moodEntries.length === 0) return <p className="text-xs text-stone-500 italic">No mood check-ins found.</p>;
                                  return (
                                    <div className="space-y-2 max-h-64 overflow-y-auto">
                                      {moodEntries.map(([dateKey, entry]) => (
                                        <div key={dateKey} className="bg-white rounded-lg p-3 border border-stone-200 group">
                                          <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0 flex-1">
                                              <div className="flex items-center gap-2 mb-0.5">
                                                <span className="text-lg">{MOOD_EMOJIS[entry.rating] || '❓'}</span>
                                                <span className="text-sm font-medium text-stone-800">{MOOD_LABELS[entry.rating] || `Rating ${entry.rating}`}</span>
                                                <span className="text-xs text-stone-500">{dateKey}</span>
                                              </div>
                                              {entry.details && (
                                                <div className="text-xs text-stone-600 mt-0.5">
                                                  {editingFreeItem?.type === 'mood' && editingFreeItem?.id === dateKey && editingFreeItem?.field === 'details' ? (
                                                    <span className="flex items-center gap-1">
                                                      <input className="text-xs border border-purple-300 rounded px-1 py-0.5 flex-1" value={editingFreeItem.value} onChange={e => setEditingFreeItem({ ...editingFreeItem, value: e.target.value })} autoFocus />
                                                      <button onClick={() => handleSaveFreeItem(targetUser.id, 'mood', dateKey, 'details', editingFreeItem.value)} className="text-xs text-green-700 px-1">Save</button>
                                                      <button onClick={() => setEditingFreeItem(null)} className="text-xs text-stone-500 px-1">Cancel</button>
                                                    </span>
                                                  ) : (
                                                    <button type="button" className="text-left cursor-pointer hover:text-purple-600" onClick={() => setEditingFreeItem({ type: 'mood', id: dateKey, field: 'details', value: entry.details })}>{entry.details}</button>
                                                  )}
                                                </div>
                                              )}
                                              {entry.factors?.length > 0 && (
                                                <div className="flex gap-1 mt-1 flex-wrap">
                                                  {entry.factors.map(f => <span key={f} className="text-xs bg-stone-100 text-stone-600 px-1.5 py-0.5 rounded-full">{f}</span>)}
                                                </div>
                                              )}
                                            </div>
                                            <button
                                              onClick={() => handleDeleteFreeItem(targetUser.id, 'mood', dateKey)}
                                              disabled={actionLoading === `free-mood-${dateKey}`}
                                              className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-50 text-red-700 hover:text-red-800 rounded flex-shrink-0"
                                              title="Delete check-in"
                                            >
                                              {actionLoading === `free-mood-${dateKey}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  );
                                })()}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Conflicts Management Section */}
        <div className="mt-8">
          <h2 className="text-xl font-bold text-stone-800 mb-4">All Conflicts</h2>
          {allConflicts.length === 0 ? (
            <div className="bg-white rounded-xl p-8 shadow-sm border border-stone-200 text-center">
              <FileText className="w-12 h-12 text-stone-300 mx-auto mb-3" />
              <p className="text-stone-500">No conflicts found</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-stone-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-stone-50 border-b border-stone-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Title</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Participants</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Created</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-200">
                    {allConflicts
                      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                      .map(conflict => (
                        <tr key={conflict.id} className="hover:bg-stone-50">
                          <td className="px-4 py-3">
                            <div className="font-medium text-stone-800 truncate max-w-xs">{conflict.title}</div>
                            <div className="text-xs text-stone-500 truncate max-w-xs">{conflict.problemStatement?.what}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              conflict.status === 'resolved' ? 'bg-green-100 text-green-700' :
                              conflict.status === 'pending-acceptance' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {(conflict.status || 'pending').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-stone-600">
                              {conflict.mentees?.length || 0} mentee{(conflict.mentees?.length || 0) !== 1 ? 's' : ''}
                              {conflict.mentor && ' • Mentor'}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-stone-500">
                            {new Date(conflict.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => deleteConflict(conflict)}
                              disabled={actionLoading === `delete-conflict-${conflict.id}`}
                              className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors disabled:opacity-50"
                              title="Delete Conflict"
                            >
                              {actionLoading === `delete-conflict-${conflict.id}` ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Reassign Modal */}
      {showReassignModal && reassignData.conflict && (
        <ModalDialog onClose={() => { setShowReassignModal(false); setReassignData({ conflict: null, role: null, currentUser: null }); }} labelledBy="reassign-user-title" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-stone-200">
              <h3 id="reassign-user-title" className="font-semibold text-stone-800">Reassign User</h3>
              <p className="text-sm text-stone-500 mt-1">
                Select a new user for the {reassignData.role === 'flyOnWall' ? 'Observer' : reassignData.role === 'omniscient' ? 'Global' : reassignData.role} role in "{reassignData.conflict.title}"
              </p>
            </div>
            <div className="p-4 max-h-96 overflow-y-auto">
              <div className="space-y-2">
                {allUsers
                  .filter(u => {
                    // Don't show the current user being replaced
                    if (u.id === reassignData.currentUser) return false;
                    // Don't show inactive users
                    if (u.isActive === false) return false;
                    // For mentee role, don't show users already in the conflict as mentees
                    if (reassignData.role === 'mentee' && reassignData.conflict.mentees?.some(m => m.id === u.id)) return false;
                    return true;
                  })
                  .map(u => (
                    <button
                      key={u.id}
                      onClick={() => reassignUser(u.id)}
                      disabled={actionLoading === `reassign-${reassignData.conflict.id}`}
                      className="w-full flex items-center gap-3 p-3 hover:bg-stone-50 rounded-lg border border-stone-200 transition-colors disabled:opacity-50"
                    >
                      <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-stone-900 font-medium">
                        {u.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div className="text-left flex-1">
                        <div className="font-medium text-stone-800">{u.name}</div>
                        <div className="text-xs text-stone-500">{u.email}</div>
                      </div>
                      <div className="text-xs text-stone-500">{getRoleLabel(u.role)}</div>
                    </button>
                  ))
                }
              </div>
            </div>
            <div className="p-4 border-t border-stone-200 flex justify-end">
              <button
                onClick={() => {
                  setShowReassignModal(false);
                  setReassignData({ conflict: null, role: null, currentUser: null });
                }}
                className="px-4 py-2 text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </ModalDialog>
      )}

      {/* Add to Conflict Modal */}
      {showAddToConflictModal && addToConflictUser && (
        <ModalDialog onClose={() => { setShowAddToConflictModal(false); setAddToConflictUser(null); }} labelledBy="add-to-conflict-title" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-stone-200">
              <h3 id="add-to-conflict-title" className="font-semibold text-stone-800">Add {addToConflictUser.name} to a Conflict</h3>
              <p className="text-sm text-stone-500 mt-1">
                Select a conflict and role to assign this user
              </p>
            </div>
            <div className="p-4 max-h-96 overflow-y-auto">
              {allConflicts.filter(c => c.status !== 'resolved').length === 0 ? (
                <p className="text-stone-500 text-center py-4">No open conflicts available</p>
              ) : (
                <div className="space-y-3">
                  {allConflicts
                    .filter(c => c.status !== 'resolved')
                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                    .map(conflict => {
                    const isAlreadyMentee = conflict.mentees?.some(m => m.id === addToConflictUser.id);
                    const isAlreadyFlyOnWall = conflict.flyOnWall?.id === addToConflictUser.id;
                    const isAlreadyOmniscient = conflict.omniscient?.id === addToConflictUser.id;
                    const isCreator = conflict.createdBy === addToConflictUser.id;
                    
                    return (
                      <div key={conflict.id} className="border border-stone-200 rounded-lg p-4">
                        <div className="font-medium text-stone-800 mb-1">{conflict.title}</div>
                        <div className="text-xs text-stone-500 mb-3">
                          {conflict.mentees?.length || 0} mentees • {conflict.status || 'In Progress'}
                        </div>
                        
                        {isCreator ? (
                          <div className="text-xs text-amber-700">User is the creator of this conflict</div>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => addUserToConflict(conflict.id, 'mentee')}
                              disabled={isAlreadyMentee || actionLoading === `add-${conflict.id}`}
                              className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                                isAlreadyMentee 
                                  ? 'bg-stone-100 text-stone-600 cursor-not-allowed' 
                                  : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                              }`}
                            >
                              {isAlreadyMentee ? '✓ Participant' : '+ Add as Participant'}
                            </button>
                            <button
                              onClick={() => addUserToConflict(conflict.id, 'flyOnWall')}
                              disabled={isAlreadyFlyOnWall || actionLoading === `add-${conflict.id}`}
                              className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                                isAlreadyFlyOnWall 
                                  ? 'bg-stone-100 text-stone-600 cursor-not-allowed' 
                                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                              }`}
                            >
                              {isAlreadyFlyOnWall ? '✓ Observer' : '+ Observer'}
                            </button>
                            <button
                              onClick={() => addUserToConflict(conflict.id, 'omniscient')}
                              disabled={isAlreadyOmniscient || actionLoading === `add-${conflict.id}`}
                              className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                                isAlreadyOmniscient 
                                  ? 'bg-stone-100 text-stone-600 cursor-not-allowed' 
                                  : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                              }`}
                            >
                              {isAlreadyOmniscient ? '✓ Global' : '+ Global'}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-stone-200 flex justify-end">
              <button
                onClick={() => {
                  setShowAddToConflictModal(false);
                  setAddToConflictUser(null);
                }}
                className="px-4 py-2 text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </ModalDialog>
      )}

      {/* Create User Modal */}
      {showCreateUserModal && (
        <ModalDialog onClose={() => { setShowCreateUserModal(false); setNewUserData({ name: '', email: '', role: 'mentee' }); }} labelledBy="create-user-title" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-4 border-b border-stone-200">
              <h3 id="create-user-title" className="font-semibold text-stone-800">Create New User</h3>
              <p className="text-sm text-stone-500 mt-1">
                Add a new user to the system
              </p>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label htmlFor="create-user-name" className="block text-sm font-medium text-stone-700 mb-1">Full Name</label>
                <input id="create-user-name"
                  type="text"
                  value={newUserData.name}
                  onChange={(e) => setNewUserData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter full name"
                  className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="create-user-email" className="block text-sm font-medium text-stone-700 mb-1">Email Address</label>
                <input id="create-user-email"
                  type="email"
                  value={newUserData.email}
                  onChange={(e) => setNewUserData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="Enter email address"
                  className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="create-user-role" className="block text-sm font-medium text-stone-700 mb-1">Role</label>
                <select id="create-user-role"
                  value={newUserData.role}
                  onChange={(e) => setNewUserData(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                >
                  {roleOptions.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="p-4 border-t border-stone-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateUserModal(false);
                  setNewUserData({ name: '', email: '', role: 'mentee' });
                }}
                className="px-4 py-2 text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={createUser}
                disabled={actionLoading === 'create-user'}
                className="px-4 py-2 bg-amber-700 hover:bg-amber-800 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {actionLoading === 'create-user' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Create User
                  </>
                )}
              </button>
            </div>
          </div>
        </ModalDialog>
      )}

      {/* Merge Users Modal */}
      {showMergeModal && mergeSourceUser && (
        <ModalDialog onClose={() => { setShowMergeModal(false); setMergeSourceUser(null); setMergeTargetUser(null); }} labelledBy="merge-users-title" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-stone-200 bg-orange-50">
              <h3 id="merge-users-title" className="font-semibold text-stone-800 flex items-center gap-2">
                <Users className="w-5 h-5 text-orange-600" />
                Merge User Accounts
              </h3>
              <p className="text-sm text-stone-600 mt-1">
                Combine duplicate accounts with the same email address
              </p>
            </div>
            
            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Source User (to be merged/deleted) */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Source Account (will be deleted)
                </label>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center text-white font-medium">
                      {mergeSourceUser.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <div className="font-medium text-stone-800">{mergeSourceUser.name}</div>
                      <div className="text-sm text-stone-500">{mergeSourceUser.email}</div>
                      <div className="text-xs text-stone-500">ID: {mergeSourceUser.id}</div>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-red-700">
                    This account will be removed after merge
                  </div>
                </div>
              </div>
              
              {/* Arrow */}
              <div className="flex justify-center">
                <ChevronDown className="w-6 h-6 text-stone-500" />
              </div>
              
              {/* Target User (to keep) */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Target Account (will be kept)
                </label>
                <div className="space-y-2">
                  {getSameEmailUsers(mergeSourceUser).map(targetOption => {
                    const isSelected = mergeTargetUser?.id === targetOption.id;
                    const targetConflicts = getUserConflicts(targetOption.id);
                    
                    return (
                      <button
                        key={targetOption.id}
                        onClick={() => setMergeTargetUser(targetOption)}
                        className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                          isSelected 
                            ? 'border-green-500 bg-green-50' 
                            : 'border-stone-200 hover:border-stone-300 hover:bg-stone-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium ${
                            isSelected ? 'bg-green-500' : 'bg-stone-400'
                          }`}>
                            {targetOption.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-stone-800">{targetOption.name}</div>
                            <div className="text-sm text-stone-500">{targetOption.email}</div>
                            <div className="text-xs text-stone-500">
                              Role: {targetOption.role} • {targetConflicts.length} conflict(s) • ID: {targetOption.id}
                            </div>
                          </div>
                          {isSelected && (
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Preview of what will happen */}
              {mergeTargetUser && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <h4 className="font-medium text-blue-800 mb-2 text-sm">What will happen:</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• All conflicts from "{mergeSourceUser.name}" will be transferred to "{mergeTargetUser.name}"</li>
                    <li>• Comments, alternatives, and other contributions will be reassigned</li>
                    <li>• The account "{mergeSourceUser.name}" will be permanently deleted</li>
                    <li>• "{mergeTargetUser.name}" will retain their login credentials</li>
                  </ul>
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-stone-200 flex justify-end gap-3 bg-stone-50">
              <button
                onClick={() => {
                  setShowMergeModal(false);
                  setMergeSourceUser(null);
                  setMergeTargetUser(null);
                }}
                className="px-4 py-2 text-stone-600 hover:bg-stone-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={mergeUsers}
                disabled={!mergeTargetUser || actionLoading === 'merge-users'}
                className="px-4 py-2 bg-orange-700 hover:bg-orange-800 disabled:bg-stone-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                {actionLoading === 'merge-users' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Merging...
                  </>
                ) : (
                  <>
                    <Users className="w-4 h-4" />
                    Merge Accounts
                  </>
                )}
              </button>
            </div>
          </div>
        </ModalDialog>
      )}

      {/* ═══ Bulk Upload Modal ═══ */}
      {showBulkUploadModal && (
        <ModalDialog onClose={resetBulkUpload} labelledBy="bulk-upload-title" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-stone-200">
              <h3 id="bulk-upload-title" className="text-lg font-bold text-stone-800 flex items-center gap-2">
                <Upload className="w-5 h-5 text-indigo-600" />
                Bulk User Upload
                {bulkStep === 'input' && ' (Step 1: Import CSV)'}
                {bulkStep === 'preview' && ' (Step 2: Preview & Validate)'}
                {bulkStep === 'complete' && ' (Complete)'}
              </h3>
              <button onClick={resetBulkUpload} className="p-1 hover:bg-stone-100 rounded" aria-label="Close bulk upload"><X className="w-5 h-5" aria-hidden="true" /></button>
            </div>

            <div className="p-6">
              {/* Step 1: Input */}
              {bulkStep === 'input' && (
                <div className="space-y-4">
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 text-sm text-indigo-800">
                    <p className="font-semibold mb-1">CSV Format</p>
                    <code className="block bg-white p-2 rounded text-xs font-mono">name,email,role,mentor_email</code>
                    <p className="mt-2">
                      <strong>Roles:</strong> participant, mentor, observer, global, admin<br />
                      <strong>mentor_email</strong> is optional. If provided, a mentor-participant relationship is created automatically.
                    </p>
                  </div>

                  <div>
                    <label htmlFor="bulk-upload-file" className="block text-sm font-medium text-stone-700 mb-1">Upload CSV file</label>
                    <input id="bulk-upload-file"
                      type="file"
                      accept=".csv,.txt"
                      onChange={handleBulkFileUpload}
                      className="block w-full text-sm text-stone-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-indigo-100 file:text-indigo-700 file:font-medium hover:file:bg-indigo-200"
                    />
                  </div>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-stone-300"></div></div>
                    <div className="relative flex justify-center text-sm"><span className="px-2 bg-white text-stone-500">or paste CSV data</span></div>
                  </div>

                  <textarea
                    value={bulkCsvText}
                    onChange={e => setBulkCsvText(e.target.value)}
                    placeholder={'name,email,role,mentor_email\nJane Doe,jane@example.com,participant,mentor@example.com\nJohn Smith,john@example.com,mentor,'}
                    rows={8}
                    className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />

                  <div className="flex justify-end">
                    <button
                      onClick={() => parseBulkCsv(bulkCsvText)}
                      disabled={!bulkCsvText.trim()}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-stone-300 text-white rounded-lg font-medium transition-colors"
                    >
                      Parse & Preview
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Preview */}
              {bulkStep === 'preview' && (
                <div className="space-y-4">
                  <div className="flex gap-3 text-sm">
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded">{bulkParsedRows.filter(r => r.status === 'ready').length} ready</span>
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded">{bulkParsedRows.filter(r => r.status === 'skip').length} skipped (existing)</span>
                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded">{bulkParsedRows.filter(r => r.status === 'error').length} errors</span>
                  </div>

                  <div className="border border-stone-200 rounded-lg overflow-x-auto max-h-96">
                    <table className="w-full text-sm">
                      <thead className="bg-stone-50 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium text-stone-600">Status</th>
                          <th className="text-left px-3 py-2 font-medium text-stone-600">Name</th>
                          <th className="text-left px-3 py-2 font-medium text-stone-600">Email</th>
                          <th className="text-left px-3 py-2 font-medium text-stone-600">Role</th>
                          <th className="text-left px-3 py-2 font-medium text-stone-600">Mentor</th>
                          <th className="text-left px-3 py-2 font-medium text-stone-600">Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkParsedRows.map((row, i) => (
                          <tr key={i} className={`border-t border-stone-100 ${row.status === 'error' ? 'bg-red-50' : row.status === 'skip' ? 'bg-yellow-50' : ''}`}>
                            <td className="px-3 py-2">
                              {row.status === 'ready' && <CheckCircle className="w-4 h-4 text-green-500" />}
                              {row.status === 'skip' && <AlertCircle className="w-4 h-4 text-yellow-700" />}
                              {row.status === 'error' && <XCircle className="w-4 h-4 text-red-700" />}
                            </td>
                            <td className="px-3 py-2">{row.name}</td>
                            <td className="px-3 py-2 font-mono text-xs">{row.email}</td>
                            <td className="px-3 py-2">{getRoleLabel(row.role)}</td>
                            <td className="px-3 py-2 font-mono text-xs">{row.mentorEmail || '—'}</td>
                            <td className="px-3 py-2 text-xs text-stone-500">{row.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex justify-between">
                    <button
                      onClick={() => setBulkStep('input')}
                      className="px-4 py-2 border border-stone-300 rounded-lg font-medium hover:bg-stone-50 transition-colors"
                    >
                      Back
                    </button>
                    <button
                      onClick={executeBulkUpload}
                      disabled={bulkLoading || bulkParsedRows.filter(r => r.status === 'ready').length === 0}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-stone-300 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                    >
                      {bulkLoading ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Creating Users...</>
                      ) : (
                        <><Upload className="w-4 h-4" /> Create {bulkParsedRows.filter(r => r.status === 'ready').length} Users</>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Complete */}
              {bulkStep === 'complete' && (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
                    <p className="text-lg font-semibold text-green-800">Upload Complete!</p>
                  </div>

                  <div className="grid grid-cols-3 gap-3 text-center text-sm">
                    <div className="bg-green-50 rounded-lg p-3">
                      <p className="text-2xl font-bold text-green-700">{bulkResults.created}</p>
                      <p className="text-green-700">Users Created</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3">
                      <p className="text-2xl font-bold text-blue-700">{bulkResults.relationships}</p>
                      <p className="text-blue-600">Relationships</p>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-3">
                      <p className="text-2xl font-bold text-yellow-700">{bulkResults.skipped}</p>
                      <p className="text-yellow-700">Skipped</p>
                    </div>
                  </div>

                  {bulkResults.errors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="font-semibold text-red-700 text-sm mb-1">Errors ({bulkResults.errors.length})</p>
                      <ul className="text-xs text-red-700 space-y-1">
                        {bulkResults.errors.map((err, i) => <li key={i}>{err}</li>)}
                      </ul>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <button
                      onClick={resetBulkUpload}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </ModalDialog>
      )}
    </div>
  );
}

// === ADMIN CONFLICT REPORT — Interactive Report (APEX-style) ===

export default UserManagementView;
